/**
 * Settings Service
 * Handles all settings-related operations
 */

import { supabase } from '../lib/supabase';

class SettingsService {
  /**
   * Get user settings
   */
  async getUserSettings(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_settings')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        // If no settings exist, create default ones
        if (error.code === 'PGRST116') {
          return await this.createUserSettings(userId);
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching user settings:', error);
      throw error;
    }
  }

  /**
   * Create default user settings
   */
  async createUserSettings(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_settings')
        .insert([{ user_id: userId }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating user settings:', error);
      throw error;
    }
  }

  /**
   * Update user settings
   */
  async updateUserSettings(userId, updates) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user settings:', error);
      throw error;
    }
  }

  /**
   * Get user profile with primary focus
   */
  async getUserProfile(userId) {
    try {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'settingsService.js:85',message:'getUserProfile entry',data:{userId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      const { data, error } = await supabase
        .schema('public')
        .from('user_profiles')
        .select('*, focuses:primary_focus_id(*)')
        .eq('id', userId)
        .single();

      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'settingsService.js:92',message:'getUserProfile result',data:{hasError:!!error,errorCode:error?.code},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Upload profile photo
   */
  async uploadProfilePhoto(userId, file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `profiles/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user_uploads')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('user_uploads')
        .getPublicUrl(filePath);

      await this.updateUserProfile(userId, { avatar_url: publicUrl });

      return publicUrl;
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      throw error;
    }
  }

  /**
   * Export all user data
   */
  async exportUserData(userId) {
    try {
      const [
        profile,
        settings,
        focuses,
        memoryItems,
        conversations,
        integrations
      ] = await Promise.all([
        this.getUserProfile(userId),
        this.getUserSettings(userId),
        supabase.schema('public').from('focuses').select('*').eq('user_id', userId),
        supabase.schema('public').from('memory_items').select('*').eq('user_id', userId),
        supabase.schema('public').from('conversations').select('*').eq('user_id', userId),
        supabase.schema('public').from('user_integrations').select('provider, provider_email, is_active, created_at').eq('user_id', userId)
      ]);

      const exportData = {
        profile: profile,
        settings: settings,
        focuses: focuses.data || [],
        memory: memoryItems.data || [],
        conversations: conversations.data || [],
        integrations: integrations.data || [],
        exportedAt: new Date().toISOString()
      };

      return exportData;
    } catch (error) {
      console.error('Error exporting user data:', error);
      throw error;
    }
  }

  /**
   * Delete all user data
   */
  async deleteAllUserData(userId) {
    try {
      // Delete from all tables - cascade should handle most
      const deletions = await Promise.allSettled([
        supabase.schema('public').from('conversations').delete().eq('user_id', userId),
        supabase.schema('public').from('memory_items').delete().eq('user_id', userId),
        supabase.schema('public').from('blocked_topics').delete().eq('user_id', userId),
        supabase.schema('public').from('focuses').delete().eq('user_id', userId),
        supabase.schema('public').from('user_integrations').delete().eq('user_id', userId),
        supabase.schema('public').from('user_settings').delete().eq('user_id', userId),
        supabase.schema('public').from('topics').delete().eq('user_id', userId),
        supabase.from('items').delete().eq('user_id', userId), // uses default deepsearch schema
        supabase.from('instagram_content').delete().eq('user_id', userId) // uses default deepsearch schema
      ]);

      return { success: true, deletions };
    } catch (error) {
      console.error('Error deleting user data:', error);
      throw error;
    }
  }
}

export default new SettingsService();

